import {
  Block,
  BlockBoundingBox,
  DocumentAnnotation,
  GetDocumentBlocksAndAnnotationsDocument,
  GetDocumentBlocksAndAnnotationsQuery,
} from "generated/graphql";
import { Dispatch, SetStateAction, useState, useRef, useEffect } from "react";

import { useAppSelector } from "redux/hooks";

import { selectUsers } from "redux/slices/client";
import { fullName } from "helpers/name-helpers";
import { runGqlQuery } from "gqlClient";
import { useSnackbar } from "notistack";
import useDebounce from "helpers/useDebounce";

export type DrawMode = "snap" | "free";
type TablesResponse = GetDocumentBlocksAndAnnotationsQuery["documents"]["document"]["page"]["tables"];
export type Table = TablesResponse[0];

export interface UseAnnotations {
  loadBlocksAndAnnotations: () => void;
  blocks: Block[];
  setBlocks: Dispatch<SetStateAction<Block[]>>;
  tables: Table[];
  documentAnnotations: DocumentAnnotation[];
  setDocumentAnnotations: Dispatch<SetStateAction<DocumentAnnotation[]>>;
  selectedDocumentAnnotation: DocumentAnnotation | undefined;
  setSelectedDocumentAnnotation: Dispatch<SetStateAction<DocumentAnnotation | undefined>>;
  annotationEditorOpen: boolean;
  setAnnotationEditorOpen: Dispatch<SetStateAction<boolean>>;
  popoverOpen: boolean;
  setPopoverOpen: Dispatch<SetStateAction<boolean>>;
  drawing: {
    enabled: boolean;
    setEnabled: Dispatch<SetStateAction<boolean>>;
    mode: DrawMode;
    setMode: Dispatch<SetStateAction<DrawMode>>;
    boundingBox: BlockBoundingBox | undefined;
    setBoundingBox: Dispatch<SetStateAction<BlockBoundingBox | undefined>>;
  };
  handleClose: () => void;
  findCreatorName: (annotation: DocumentAnnotation) => string | undefined;
  findEditorName: (annotation: DocumentAnnotation) => string | undefined;
}

function useAnnotations({ documentId, pageNumber }: { documentId: number | undefined; pageNumber: number }) {
  const { enqueueSnackbar } = useSnackbar();
  const [documentAnnotations, setDocumentAnnotations] = useState<DocumentAnnotation[]>([]);
  const [selectedDocumentAnnotation, setSelectedDocumentAnnotation] = useState<DocumentAnnotation | undefined>(
    undefined
  );
  const [drawnBoundingBox, setDrawnBoundingBox] = useState<BlockBoundingBox | undefined>(undefined);
  const [annotationEditorOpen, setAnnotationEditorOpen] = useState<boolean>(false);
  const [drawingEnabled, setDrawingEnabled] = useState<boolean>(false);
  const [drawingMode, setDrawingMode] = useState<DrawMode>("snap");
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);

  const users = useAppSelector(selectUsers);

  const latestRequestId = useRef(0);

  const debouncedPageNumber = useDebounce(pageNumber, 200);

  const reset = () => {
    setTables([]);
    setBlocks([]);
    setDocumentAnnotations([]);
    setAnnotationEditorOpen(false);
    setDrawingEnabled(false);
    setSelectedDocumentAnnotation(undefined);
    setDrawnBoundingBox(undefined);
    setPopoverOpen(false);
  };

  useEffect(() => {
    reset();
  }, [pageNumber]);

  const loadBlocksAndAnnotations = () => {
    if (!documentId) return;
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    runGqlQuery(GetDocumentBlocksAndAnnotationsDocument, {
      documentId,
      pageNumber: debouncedPageNumber,
    })
      .then((result) => {
        if (latestRequestId.current === requestId) {
          setTables(result.documents.document.page.tables || []);
          setBlocks(result.documents.document.page.blocks || []);
          setDocumentAnnotations(
            result.documents.document.page.annotations.sort((a, b) => a.boundingBox.top - b.boundingBox.top)
          );
        }
      })
      .catch((e) => {
        enqueueSnackbar("There was a problem loading the annotations. Please try again later.", {
          variant: "error",
        });
        console.error(`Error loading annotations for ${documentId}, ${debouncedPageNumber}`, e);
        setTables([]);
        setBlocks([]);
        setDocumentAnnotations([]);
      });
  };

  useEffect(() => {
    loadBlocksAndAnnotations();
  }, [debouncedPageNumber, documentId]);

  return {
    loadBlocksAndAnnotations,
    blocks,
    setBlocks,
    tables,
    documentAnnotations,
    setDocumentAnnotations,
    selectedDocumentAnnotation,
    setSelectedDocumentAnnotation,
    annotationEditorOpen,
    setAnnotationEditorOpen,
    popoverOpen,
    setPopoverOpen,
    drawing: {
      enabled: drawingEnabled,
      setEnabled: setDrawingEnabled,
      mode: drawingMode,
      setMode: setDrawingMode,
      boundingBox: drawnBoundingBox,
      setBoundingBox: setDrawnBoundingBox,
    },
    handleClose: () => {
      setAnnotationEditorOpen(false);
      setDrawingEnabled(false);
      setDrawnBoundingBox(undefined);
      setSelectedDocumentAnnotation(undefined);
      setPopoverOpen(false);
    },
    findCreatorName: (annotation: DocumentAnnotation) => {
      const user = users.find((u) => u.id === annotation.createdBy);
      return user ? fullName(user) : undefined;
    },
    findEditorName: (annotation: DocumentAnnotation) => {
      const user = users.find((u) => u.id === annotation.editedBy);
      return user ? fullName(user) : undefined;
    },
  };
}

export default useAnnotations;
